import { useDebounce } from './useDebounce';

export interface DebouncedSearchOptions {
  /** Wait after the last keystroke before searching. */
  delay?: number;
  /** Shorter terms don't search; the list stays unfiltered. */
  minLength?: number;
}

/**
 * The term a search box should query with, given what is typed now (`term`,
 * trimmed) and what has settled (`settled`). Clearing the box applies at once
 * rather than after the wait, and a term below `minLength` doesn't search.
 */
export function debouncedSearchTerm(term: string, settled: string, minLength = 1): string {
  if (!term) return '';
  return settled.length >= minLength ? settled : '';
}

/**
 * A search box's query term: trimmed, so a trailing space or retyping the same
 * text doesn't send another request, and debounced so typing sends one search.
 */
export function useDebouncedSearch(
  input: string,
  { delay = 300, minLength = 1 }: DebouncedSearchOptions = {}
): string {
  const term = input.trim();
  const settled = useDebounce(term, delay);
  return debouncedSearchTerm(term, settled, minLength);
}
